// =====================================================================
// landingContentDefaults — durable, code-shipped copy for landing-page
// content that must survive an empty/lost CMS store.
//
// Why this exists: the client set the four disease definitions through
// Settings → Landing Content, and they vanished after a sync/rebuild —
// that editor's content is ONLY file-backed (data/landing-content.json,
// see services/auth/routes/public.js's GET/POST /landing-content), so a
// deploy that lands without carrying that file over reverts the section
// to whatever hardcoded copy shipped in the bundle. Disease names and
// descriptions are durable *code*, not CMS-only content, from now on:
// DISEASE_DEFINITIONS below is the single source of truth for them, and
// resolveDiseaseCopy() is how LandingPage.jsx merges a saved CMS
// override on top — per FIELD, never as a whole-object replace, so a
// saved item with only `description` set doesn't blank out `name`, and
// (the actual bug) an empty/missing CMS item after a rebuild falls all
// the way back to this file instead of rendering blank.
// =====================================================================

export const DISEASE_DEFINITIONS = Object.freeze({
  malaria: Object.freeze({
    name: 'Malaria',
    description: 'A mosquito-borne parasitic disease caused by Plasmodium species.',
  }),
  flu: Object.freeze({
    name: 'Flu syndrome',
    description: 'An acute upper respiratory illness caused by influenza viruses.',
  }),
  pneumonia: Object.freeze({
    name: 'Pneumonia',
    description: 'Lung inflammation caused by infectious agents, particularly bacteria.',
  }),
  diarrhea: Object.freeze({
    name: 'Diarrhea',
    description: 'The passage of watery stools, usually from waterborne or foodborne infections.',
  }),
})

// Same non-destructive-empty-value convention as teamCardLogic.mjs's
// isPresentString — a whitespace-only saved value is treated as absent,
// not as an intentional override to blank copy.
function isPresentString(value) {
  return typeof value === 'string' && value.trim().length > 0
}

// Resolves the display copy for one disease card.
//   key         — 'malaria' | 'flu' | 'pneumonia' | 'diarrhea'
//   contentItem — whatever content?.diseases?.items?.[key] the CMS
//                 returned; may be undefined, null, or a partial object
//                 with only some fields ever saved.
// Returns { name, description }. A saved, non-blank field on
// contentItem wins; otherwise the durable default from
// DISEASE_DEFINITIONS is used. An unknown key resolves to '' for both
// fields rather than throwing, since this runs against data driven by
// the same ['malaria','flu','pneumonia','diarrhea'] list LandingPage.jsx
// already iterates.
export function resolveDiseaseCopy(key, contentItem) {
  const fallback = DISEASE_DEFINITIONS[key] || { name: '', description: '' }
  const override =
    contentItem && typeof contentItem === 'object' && !Array.isArray(contentItem)
      ? contentItem
      : {}

  return {
    name: isPresentString(override.name) ? override.name : fallback.name,
    description: isPresentString(override.description)
      ? override.description
      : fallback.description,
  }
}

// =====================================================================
// isSafeHttpUrl — the client-side half of the partner-website allow-list.
//
// Why this exists: services/auth/routes/public.js's POST /images accepts a
// full `partnerRoster` array replace from anyone holding manage_images —
// including each entry's `website` — and LandingPage.jsx's Partners
// section renders that value as a live, clickable `<a href>` for every
// anonymous visitor of a public Ministry site. Without a scheme allow-list
// a `javascript:` (or `data:`, `vbscript:`) "website" becomes a stored-XSS
// payload that fires on click for the whole public. This is deliberately
// an ALLOW-list (http(s) only), not a denylist of dangerous schemes —
// denylists miss the next scheme nobody thought to block.
//
// The server enforces the identical rule independently in public.js
// (defense in depth: a client-only guard is bypassed by anyone calling the
// API directly) — keep both in sync if this rule ever changes.
export function isSafeHttpUrl(url) {
  return typeof url === 'string' && /^https?:\/\//i.test(url.trim())
}
